// API Client utility using native Fetch API (migrated from Axios)
// Based on prosci_admin pattern with prosci_portal-specific adaptations

import { apiHealthMonitor } from './api-health-monitor';
import type { GuidedTurnIntent } from '../features/chat/guided/types';

// Utility function to convert camelCase keys to snake_case for Rails API
const toSnakeCase = (str: string): string => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

// Recursively transform object keys from camelCase to snake_case
const transformKeysToSnakeCase = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(transformKeysToSnakeCase);
  }

  // Only transform plain objects, not Date, RegExp, etc.
  if (typeof obj === 'object' && obj.constructor === Object && Object.getPrototypeOf(obj) === Object.prototype) {
    const transformed: any = {};
    for (const [key, value] of Object.entries(obj)) {
      const snakeKey = toSnakeCase(key);
      transformed[snakeKey] = transformKeysToSnakeCase(value);
    }
    return transformed;
  }

  return obj;
};

// Configure API base URL - use Vite proxy in development for cookie support
// The proxy is configured in vite.config.ts to forward /api/* to the Rails API
// This allows cookies to be set on the same origin (localhost:5173)
const API_BASE_URL = import.meta.env.MODE === 'development' ? '' : import.meta.env.VITE_PROSCI_CONNECT_API_URL;

// Export helper function to get full API URL - use this for any direct fetch calls
// This ensures consistent URL construction across the app
export function getApiUrl(path: string): string {
  return `${API_BASE_URL}${path}`;
}

// CSRF token storage
let csrfToken: string | null = null;

// Function to get CSRF token from API
async function getCsrfToken(): Promise<string> {
  if (csrfToken) {
    return csrfToken;
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/auth/csrf_token`, {
      credentials: 'include',  // Include cookies
    });

    if (response.ok) {
      const data = await response.json();
      csrfToken = data.csrf_token;
      return csrfToken!;
    }
  } catch (error) {
    console.error('Failed to fetch CSRF token:', error);
  }

  return '';
}

// Clear CSRF token (call this on logout)
export function clearCsrfToken(): void {
  csrfToken = null;
}

// Set CSRF token (called after authentication)
export function setCsrfToken(token: string): void {
  csrfToken = token;
}

// Get current CSRF token (for debugging)
export function getCurrentCsrfToken(): string | null {
  return csrfToken;
}

// Base headers
const baseHeaders: Record<string, string> = {
  'Content-Type': 'application/json',
  'Accept': 'application/json',
};

// Error class for API errors
export class ApiClientError extends Error {
  constructor(
    message: string,
    public status?: number,
    public response?: any
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

// Helper function to check if current route is public
const isPublicRoute = (pathname: string) => {
  const publicRoutes = [
    '/register',
    '/registration_confirmation',
    '/unauthorized',
    '/prohibited',
    '/terms',
    '/auth/callback',
    '/users/verify_email_change',
    '/users/verify_recovery_email'
  ];

  return publicRoutes.some(route => pathname.startsWith(route));
};

// Track if we're currently handling a session expiration
let handlingSessionExpiration = false;

// Helper to check if we're handling session expiration
export function isHandlingSessionExpiration(): boolean {
  return handlingSessionExpiration;
}

// Contract with the backend: title of the 401 body when a session has been
// revoked (OIDC backchannel logout, admin-forced logout, password change).
// Backend constant: Api::Authentication::SESSION_REVOKED_MESSAGE
export const SESSION_REVOKED_MESSAGE = 'Session has been revoked';

// Dispatched when the backend reports the current session as revoked. The
// AuthContext listens for this and drives a full Auth0 logout so the browser
// can't silent-reauthenticate and land back in a signed-in state.
export const SESSION_REVOKED_EVENT = 'auth:session-revoked';

// Handle session expiration (401 errors)
async function handleSessionExpired(): Promise<void> {
  // Don't handle 401s on public routes
  if (isPublicRoute(window.location.pathname)) {
    return;
  }

  // Prevent multiple simultaneous calls
  if (handlingSessionExpiration) {
    return;
  }
  handlingSessionExpiration = true;

  try {
    // Session expired - reload current page to trigger ProtectedRoute auth
    // This preserves the current path so user returns here after login
    // Auth0 SDK handles session refresh internally, so we just need to
    // trigger a re-authentication flow via ProtectedRoute
    window.location.reload();
  } finally {
    handlingSessionExpiration = false;
  }
}

// The session was explicitly revoked (not just expired). Fire an event so
// AuthContext can call Auth0's /v2/logout and terminate the SSO cookie —
// otherwise the Auth0 SDK silently re-authenticates and puts the user back
// in with a fresh, un-revoked jti.
function handleSessionRevoked(): void {
  if (handlingSessionExpiration) return;
  handlingSessionExpiration = true;
  window.dispatchEvent(new CustomEvent(SESSION_REVOKED_EVENT));
}

// Generic fetch wrapper
async function fetchWithRetry<T>(
  url: string,
  config: RequestInit,
  isRetry = false,
  skip401Handling = false
): Promise<T> {
  const method = config.method || 'GET';

  try {
    const response = await fetch(url, config);

    // Health monitoring: record outcome based on response status.
    // Suppress while a session event is in flight — otherwise Auth0's logout
    // redirect can abort in-flight fetches fast enough to trip the 3-failure
    // threshold before the browser actually navigates.
    if (
      !handlingSessionExpiration &&
      apiHealthMonitor.shouldMonitor() &&
      !apiHealthMonitor.isExcludedEndpoint(url)
    ) {
      if (response.ok || (response.status >= 400 && response.status < 500)) {
        // 2xx and 4xx responses indicate the API is reachable
        apiHealthMonitor.recordSuccess();
      } else if (apiHealthMonitor.isServerError(response.status)) {
        // 5xx errors indicate server issues
        apiHealthMonitor.recordFailure(response.status, url, method);
        if (apiHealthMonitor.shouldRedirect() && await apiHealthMonitor.handleTrip()) {
          // Return a promise that never resolves to prevent further processing
          return new Promise(() => {});
        }
      }
    }

    // Handle 401 Unauthorized. Revocation is handled unconditionally — the
    // SESSION_REVOKED_EVENT it dispatches drives a full Auth0 logout, which
    // is always the right response to an explicit revocation. The natural-
    // expiry reload path is gated by skip401Handling so callers like
    // validateSession (used during init) don't trigger a reload loop.
    if (response.status === 401 && !isRetry) {
      // Peek at the body to distinguish an explicit revocation (password
      // change, admin logout, OIDC backchannel) from a naturally expired
      // JWT that Auth0 can silently refresh.
      const errorBody = await response.clone().json().catch(() => null);
      const isRevoked = errorBody?.errors?.some(
        (err: { title?: string }) => err?.title === SESSION_REVOKED_MESSAGE
      );

      if (isRevoked) {
        handleSessionRevoked();
        // Hang the promise so callers (e.g. validateSession in AuthContext)
        // can't proceed past the await and silently re-authenticate via
        // createBackendSession before the Auth0 logout redirect lands.
        return new Promise(() => {});
      }

      if (!skip401Handling) {
        // Try to refresh session and retry once
        await handleSessionExpired();

        // If we're still here (didn't redirect), retry the request
        if (!handlingSessionExpiration) {
          return fetchWithRetry<T>(url, config, true, skip401Handling);
        }
      }
    }

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new ApiClientError(
        errorBody?.error || errorBody?.message || `Request failed with status ${response.status}`,
        response.status,
        errorBody
      );
    }

    // Handle empty responses (204 No Content)
    if (response.status === 204) {
      return {} as T;
    }

    return await response.json();
  } catch (error) {
    // Abort errors are NOT network failures - user navigated away or request was cancelled
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }

    // Network error - API is unreachable.
    // Skip if we're handling a session event (logout redirect aborts fetches
    // in Chrome as TypeError "Failed to fetch", which would falsely trip
    // the health monitor before the browser navigates).
    if (
      !handlingSessionExpiration &&
      !(error instanceof ApiClientError) &&
      apiHealthMonitor.shouldMonitor() &&
      !apiHealthMonitor.isExcludedEndpoint(url)
    ) {
      apiHealthMonitor.recordFailure('network', url, method);
      if (apiHealthMonitor.shouldRedirect() && await apiHealthMonitor.handleTrip()) {
        // Return a promise that never resolves to prevent further processing
        return new Promise(() => {});
      }
    }
    throw error;
  }
}

// Create fetch-based API client (replacing Axios)
const apiClient = {
  // GET request
  get: async <T = any>(url: string, config?: { params?: Record<string, any>; skip401Handling?: boolean; responseType?: 'json' | 'blob' }): Promise<{ data: T; headers?: Record<string, string> }> => {
    const params = new URLSearchParams(config?.params || {});
    const queryString = params.toString();
    const fullUrl = `${API_BASE_URL}${url}${queryString ? `?${queryString}` : ''}`;

    // Binary downloads (e.g. guided artifact exports) bypass fetchWithRetry,
    // which always parses the body as JSON — that throws on a docx/xlsx
    // payload. Raw fetch also lets us read Content-Disposition for the
    // server-suggested filename, which fetchWithRetry's parsed-body-only
    // return value can't expose.
    if (config?.responseType === 'blob') {
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: baseHeaders,
        credentials: 'include',
        cache: 'no-store',
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        throw new ApiClientError(
          errorBody?.error || errorBody?.message || `Request failed with status ${response.status}`,
          response.status,
          errorBody
        );
      }

      const blob = await response.blob();
      return {
        data: blob as unknown as T,
        headers: { 'content-disposition': response.headers.get('content-disposition') || '' },
      };
    }

    const data = await fetchWithRetry<T>(fullUrl, {
      method: 'GET',
      headers: baseHeaders,
      credentials: 'include',
      cache: 'no-store',  // Prevent browser caching of GET requests
    }, false, config?.skip401Handling);

    return { data };
  },

  // POST request
  post: async <T = any>(url: string, data?: any, config?: { params?: Record<string, any>; skip401Handling?: boolean }): Promise<{ data: T }> => {
    const token = await getCsrfToken();
    const params = new URLSearchParams(config?.params || {});
    const queryString = params.toString();
    const fullUrl = `${API_BASE_URL}${url}${queryString ? `?${queryString}` : ''}`;

    const responseData = await fetchWithRetry<T>(fullUrl, {
      method: 'POST',
      headers: {
        ...baseHeaders,
        'X-CSRF-Token': token,
      },
      credentials: 'include',
      body: data ? JSON.stringify(data) : undefined,
    }, false, config?.skip401Handling);

    return { data: responseData };
  },

  // PATCH request
  patch: async <T = any>(url: string, data?: any, config?: { params?: Record<string, any>; skip401Handling?: boolean }): Promise<{ data: T }> => {
    const token = await getCsrfToken();
    const params = new URLSearchParams(config?.params || {});
    const queryString = params.toString();
    const fullUrl = `${API_BASE_URL}${url}${queryString ? `?${queryString}` : ''}`;

    const responseData = await fetchWithRetry<T>(fullUrl, {
      method: 'PATCH',
      headers: {
        ...baseHeaders,
        'X-CSRF-Token': token,
      },
      credentials: 'include',
      body: data ? JSON.stringify(data) : undefined,
    }, false, config?.skip401Handling);

    return { data: responseData };
  },

  // DELETE request
  delete: async <T = any>(url: string, config?: { params?: Record<string, any> }): Promise<{ data: T }> => {
    const token = await getCsrfToken();
    const params = new URLSearchParams(config?.params || {});
    const queryString = params.toString();
    const fullUrl = `${API_BASE_URL}${url}${queryString ? `?${queryString}` : ''}`;

    const data = await fetchWithRetry<T>(fullUrl, {
      method: 'DELETE',
      headers: {
        ...baseHeaders,
        'X-CSRF-Token': token,
      },
      credentials: 'include',
    });

    return { data };
  },
};

// Prosci Member badge (Credly) status returned by /api/credly_badge
export interface MemberBadgeStatus {
  eligible: boolean;
  opted_in: boolean;
  state: string | null;
  expires_at: string | null;
  email: string | null;
  image_url: string | null;
  badge_url: string | null;
  failed: boolean;
}

// API methods matching the Connect API endpoints
export const api = {
  // User endpoints (skip 401 handling during auth initialization to prevent redirect loops)
  getCurrentUser: () => apiClient.get('/api/users/current', { params: { profile: 'portal_home', include: 'account', virtual_include: 'dashboard_products' }, skip401Handling: true }),
  getProductDrawer: () => apiClient.get('/api/product_drawer'),
  getPickUpWork: () => apiClient.get('/api/dashboard/pick_up_work'),
  getTrendingCommunityPosts: () => apiClient.get('/api/dashboard/community/trending_posts'),
  getUpcomingCommunityEvents: () => apiClient.get('/api/dashboard/community/upcoming_events'),
  trackResourceView: (attributes: {
    contentfulEntryId: string
    locale: string
    hubType: string
    canonicalPath: string
    title: string
    description?: string
  }) => apiClient.post('/api/resource_views', {
    data: { type: 'resource_view', attributes: transformKeysToSnakeCase(attributes) },
  }),
  getUserById: (id: string) => apiClient.get(`/api/users/${id}`),

  // Workspace endpoints (internal vertical slice — UI gated by the 'workspaces' product feature)
  getWorkspaces: () => apiClient.get('/api/workspaces'),
  activateWorkspace: (workspaceId: string | number) =>
    apiClient.post(`/api/workspaces/${workspaceId}/activate`, {}),

  getQAUser: (id: string) => apiClient.get(`/api/users/qa/${id}`, { params: { profile: 'portal_home', include: 'account', virtual_include: 'dashboard_products' }, skip401Handling: true }),
  updateUser: (id: string, data: any) => apiClient.patch(`/api/users/${id}`, data),
  updateUserProfile: (userId: string, profile: any) => {
    // Transform all camelCase keys to snake_case automatically
    const transformedProfile = transformKeysToSnakeCase(profile);

    // Send in JSONAPI format expected by Connect API
    return apiClient.patch(`/api/users/${userId}`, {
      data: {
        type: 'user',
        attributes: transformedProfile
      }
    });
  },
  updateUserEmail: (userId: string, userInfo: { newEmail: string; existingPassword: string }) =>
    apiClient.patch(`/api/users/${userId}/email_change_request`, {
      data: {
        type: 'user',
        attributes: {
          new_email: userInfo.newEmail,
          existing_password: userInfo.existingPassword
        }
      }
    }),
  passwordReset: (userId: string, password: any) =>
    apiClient.post(`/api/users/${userId}/reset_password`, password),
  // Recovery email (identity-backed canonical email, never used for authentication)
  requestRecoveryEmail: (userId: string, email: string) =>
    apiClient.patch(`/api/users/${userId}/recovery_email`, {
      data: {
        type: 'user',
        attributes: { recovery_email: email }
      }
    }),
  removeRecoveryEmail: (userId: string) =>
    apiClient.delete(`/api/users/${userId}/recovery_email`),
  resendRecoveryEmailVerification: (userId: string) =>
    apiClient.post(`/api/users/${userId}/recovery_email/resend`),
  // Logged-out verification page: never trigger the global 401 handler
  // (session reload / Auth0 logout), since an unauthenticated visitor
  // hitting this link is expected, not a session failure.
  verifyRecoveryEmail: (token: string) =>
    apiClient.patch('/api/users/verify_recovery_email', {
      data: {
        attributes: {
          recovery_email_token: token
        }
      }
    }, { skip401Handling: true }),
  verifyEmailChange: (token: string) =>
    apiClient.patch('/api/users/verify_email_change', {
      data: {
        attributes: {
          change_token: token
        }
      }
    }),
  acceptTerms: (productCode: string) =>
    apiClient.post('/api/users/accept_terms', {
      data: {
        type: 'user',
        attributes: {
          product_code: productCode
        }
      }
    }),

  // Programs endpoints (from Contentful)
  getPrograms: (type = 'all', locale = 'en-US') => {
    return apiClient.get('/api/contentful/programs', { params: { type, locale } });
  },
  getProgram: (slug: string, locale = 'en-US') =>
    apiClient.get(`/api/contentful/programs/${slug}`, { params: { locale } }),
  // The API authorizes a unit through its parent program, so the program slug is required.
  getProgramUnit: (slug: string, programSlug: string, locale = 'en-US') =>
    apiClient.get(`/api/contentful/program-units/${slug}`, { params: { locale, program: programSlug } }),
  getUserPrograms: () => apiClient.get('/api/user_programs'),

  // Apps endpoints
  getUserApps: () => apiClient.get('/api/user_apps'),

  // App Copy endpoints (translations/content) - These come from Contentful now
  getAppCopy: (locale = 'en-US') =>
    apiClient.get('/api/contentful/app-copy', { params: { type: 'app', locale } }),
  getHubAppCopy: (locale = 'en-US') =>
    apiClient.get('/api/contentful/app-copy', { params: { type: 'hub', locale } }),
  getPathwaysAppCopy: (locale = 'en-US') =>
    apiClient.get('/api/contentful/app-copy', { params: { type: 'pathways', locale } }),
  getDashboardAnnouncement: (locale = 'en-US') =>
    apiClient.get('/api/contentful/dashboard-announcement', { params: { locale } }),
  getDashboardFeatured: (locale = 'en-US') =>
    apiClient.get('/api/contentful/dashboard-featured', { params: { locale } }),

  // Bookmarks endpoints
  getBookmarks: (userId: string) =>
    apiClient.get('/api/bookmarks', { params: { user_id: userId } }),
  createBookmark: (bookmarkData: any, currentUserId: string) => {
    // Connect API expects JSON API format
    const payload = {
      data: {
        attributes: {
          contentful_program_name: bookmarkData.contentfulProgramName,
          program_type: bookmarkData.programType,
          data: bookmarkData.data
        },
        relationships: {
          user: {
            data: { id: currentUserId }
          }
        }
      }
    };
    return apiClient.post('/api/bookmarks', payload);
  },
  updateBookmark: (id: string, data: any) => apiClient.patch(`/api/bookmarks/${id}`, data),
  deleteBookmark: (id: string) => {
    // Delete endpoint now uses session-based auth
    return apiClient.delete(`/api/bookmarks/${id}`);
  },

  // Search endpoints
  search: (query: string, filters: Record<string, any> = {}) =>
    apiClient.get('/api/search', { params: { q: query, ...filters } }),

  // Kaiya AI endpoints
  createAIChat: (data: any) => apiClient.post('/api/kaiya/chats', data),
  getAIChats: () => apiClient.get('/api/kaiya/chats'),
  getAIChat: (id: string) => apiClient.get(`/api/kaiya/chats/${id}`),
  deleteAIChat: (id: string) => apiClient.delete(`/api/kaiya/chats/${id}`),
  sendAIMessage: (chatId: string, message: string) =>
    apiClient.post(`/api/kaiya/chats/${chatId}/messages`, { message }),

  setDashboardExperience: (experience: 'legacy' | 'new') =>
    apiClient.patch('/api/dashboard/experience', { experience }),

  // Kaiya memory endpoints (profile memory only — episodic memory is never exposed to users)
  getKaiyaMemory: () => apiClient.get('/api/kaiya_memory'),
  kaiyaMemoryOptIn: () => apiClient.patch('/api/kaiya_memory/opt_in'),
  kaiyaMemoryOptOut: () => apiClient.patch('/api/kaiya_memory/opt_out'),
  updateKaiyaMemoryProfile: (profile: string) =>
    apiClient.patch('/api/kaiya_memory/profile', { profile }),
  deleteAllKaiyaMemory: () => apiClient.delete('/api/kaiya_memory'),
  // Analytics only: the three memory signals the server cannot observe.
  // Fire-and-forget — a failed beacon must never disturb the UI.
  trackKaiyaMemoryEvent: (event: string) =>
    apiClient.post('/api/kaiya_memory/track', { event }).catch(() => {}),

  // Contentful proxy endpoints
  getContentfulProgram: (programId: string) =>
    apiClient.get(`/api/contentful/programs/${programId}`),
  getContentfulEntry: (entryId: string) =>
    apiClient.get(`/api/contentful/entries/${entryId}`),
  searchContentful: (query: string) =>
    apiClient.get('/api/contentful/search', { params: { query } }),
  getKaiyaPrompts: (locale = 'en-US') =>
    apiClient.get('/api/contentful/kaiya-prompts', { params: { locale } }),
  getProductDisplayConfig: (locale = 'en-US') =>
    apiClient.get('/api/contentful/product-display-config', { params: { locale } }),

  // Products endpoints
  getProducts: () => apiClient.get('/api/products'),
  getUserProducts: () => apiClient.get('/api/user_products'),

  // Locale endpoints
  getLocales: () => apiClient.get('/api/locales'),
  getCurrentLocale: () => apiClient.get('/api/locale'),
  setLocale: (locale: string) => apiClient.post('/api/locale', { locale }),

  // Related Files endpoints
  getRelatedFiles: (userId: string) =>
    apiClient.get('/api/related_files', { params: { user_id: userId } }),

  // Auth0 endpoints
  getUserByAuth0Sub: (sub: string) =>
    apiClient.get('/api/users/auth0', { params: { sub } }),
  getUserByEmail: (email: string) =>
    apiClient.get('/api/users/email', { params: { email } }),

  // Account endpoints
  getAccount: (id: string) => apiClient.get(`/api/accounts/${id}`),
  getAccountBySlug: (slug: string) =>
    apiClient.get('/api/accounts/find/slug', { params: { slug } }),
  getAccountByDomain: (domain: string) =>
    apiClient.get('/api/accounts/find/domain', { params: { domain } }),
  sendNewRegistrationEmail: (accountId: string, userEmail: string) =>
    apiClient.post(`/api/accounts/${accountId}/send_notification`, {
      data: {
        type: 'account',
        attributes: {
          email: userEmail
        }
      }
    }),

  // Session endpoints
  createSession: (accessToken: string) =>
    apiClient.post('/api/sessions', { access_token: accessToken }),
  destroySession: () => apiClient.delete('/api/sessions'),

  // AI Services endpoints
  getUserChatSessions: () => apiClient.get('/api/ai_services/get_user_chat_sessions'),
  getChatSessionMessages: (chatSessionId: string) =>
    apiClient.get('/api/ai_services/get_chat_session_messages', {
      params: {
        chat_session_id: chatSessionId
      }
    }),
  // Guided Use Cases (a mode inside Kaiya)
  getGuidedUseCases: (locale?: string) =>
    apiClient.get('/api/ai_services/get_guided_use_cases', { params: locale ? { locale } : undefined }),
  getGuidedUseCase: (key: string, locale?: string, chatSessionId?: string) =>
    apiClient.get('/api/ai_services/get_guided_use_case', {
      params: { key, ...(locale ? { locale } : {}), ...(chatSessionId ? { chat_session_id: chatSessionId } : {}) }
    }),
  startGuidedSession: (useCaseKey: string, locale?: string, entryPoint?: string) =>
    apiClient.post('/api/ai_services/start_guided_session', {
      data: { type: 'guided_use_case', attributes: { use_case_key: useCaseKey, locale, entry_point: entryPoint } }
    }),
  sendGuidedTurn: (
    chatSessionId: string,
    message: string,
    files?: Array<{ name: string; size: number; type: string; base64: string }>,
    intent?: GuidedTurnIntent
  ) =>
    apiClient.post('/api/ai_services/guided_turn', {
      data: {
        type: 'guided_use_case',
        attributes: {
          chat_session_id: chatSessionId,
          message,
          ...(files && files.length ? { files } : {}),
          ...(intent ? { intent } : {})
        }
      }
    }),
  // Jump back to an already-completed milestone to review/refine it (reopens
  // complete sessions).
  focusGuidedMilestone: (chatSessionId: string, milestoneIndex: number) =>
    apiClient.post('/api/ai_services/focus_guided_milestone', {
      data: { type: 'guided_use_case', attributes: { chat_session_id: chatSessionId, milestone_index: milestoneIndex } }
    }),
  // Manual canvas edit of one slot's content (formatting-only — the AI's next
  // turn can still overwrite it).
  updateGuidedSlot: (chatSessionId: string, slotId: string, draftBlock: Record<string, unknown>) =>
    apiClient.patch('/api/ai_services/update_guided_slot', {
      data: { type: 'guided_use_case', attributes: { chat_session_id: chatSessionId, slot_id: slotId, draft_block: draftBlock } }
    }),
  // Revert the canvas to how it looked right before the last turn or manual
  // edit (single-level undo).
  undoGuidedTurn: (chatSessionId: string) =>
    apiClient.post('/api/ai_services/undo_guided_turn', {
      data: { type: 'guided_use_case', attributes: { chat_session_id: chatSessionId } }
    }),
  // Selecting an impacted group's tab moves the pointer to that group's first
  // unfinished section, so what the practitioner types next lands where they
  // are looking.
  focusGuidedGroup: (chatSessionId: string, groupId: string) =>
    apiClient.post('/api/ai_services/focus_guided_group', {
      data: { type: 'guided_use_case', attributes: { chat_session_id: chatSessionId, group_id: groupId } }
    }),
  addGuidedRow: (chatSessionId: string, label: string) =>
    apiClient.post('/api/ai_services/add_guided_row', {
      data: { type: 'guided_use_case', attributes: { chat_session_id: chatSessionId, label } }
    }),
  chooseGuidedUpstream: (chatSessionId: string, useCaseKey: string, upstreamSessionId: number) =>
    apiClient.post('/api/ai_services/choose_guided_upstream', {
      data: {
        type: 'guided_use_case',
        attributes: { chat_session_id: chatSessionId, use_case_key: useCaseKey, upstream_session_id: upstreamSessionId }
      }
    }),
  renameGuidedRow: (chatSessionId: string, slotId: string, label: string) =>
    apiClient.patch('/api/ai_services/rename_guided_row', {
      data: { type: 'guided_use_case', attributes: { chat_session_id: chatSessionId, slot_id: slotId, label } }
    }),
  removeGuidedRow: (chatSessionId: string, slotId: string) =>
    apiClient.delete('/api/ai_services/remove_guided_row', {
      params: { chat_session_id: chatSessionId, slot_id: slotId }
    }),
  // Thumbs up/down (+ optional free-text) on a single canvas section.
  sendGuidedSectionFeedback: (chatSessionId: string, slotId: string, helpful: boolean, feedback?: string) =>
    apiClient.post('/api/ai_services/send_guided_section_feedback', {
      data: {
        type: 'guided_use_case',
        attributes: { chat_session_id: chatSessionId, slot_id: slotId, helpful, feedback: feedback || null }
      }
    }),
  // Download the finished artifact as a file (docx/xlsx per the use case's outputs).
  exportGuidedArtifact: (chatSessionId: string, format: string) =>
    apiClient.get('/api/ai_services/export_guided_artifact', {
      params: { chat_session_id: chatSessionId, format },
      responseType: 'blob'
    }),
  // Mint (idempotently) the session's read-only share token.
  shareGuidedSession: (chatSessionId: string, visibility: 'workspace' | 'public' = 'workspace') =>
    apiClient.post('/api/ai_services/share_guided_session', {
      data: { type: 'guided_use_case', attributes: { chat_session_id: chatSessionId, visibility } }
    }),
  unshareGuidedSession: (chatSessionId: string) =>
    apiClient.delete('/api/ai_services/unshare_guided_session', {
      params: { chat_session_id: chatSessionId }
    }),
  // Fetch a shared artifact by token (read-only view).
  getSharedGuidedArtifact: (token: string) =>
    apiClient.get('/api/ai_services/get_shared_guided_artifact', { params: { token } }),
  getGuidedPublicLink: (token: string) =>
    apiClient.get(`/api/guided_public_links/${encodeURIComponent(token)}`, { skip401Handling: true }),
  // Download a shared artifact as a file (docx/xlsx per the use case's outputs),
  // scoped by share token rather than session ownership.
  exportSharedGuidedArtifact: (token: string, format: string) =>
    apiClient.get('/api/ai_services/export_shared_guided_artifact', {
      params: { token, format },
      responseType: 'blob'
    }),
  // Voice-to-text: upload a recorded audio blob, get back the transcript.
  // Bypasses apiClient.post, which always JSON.stringifies its body — that
  // would silently collapse the FormData into "{}". Mirrors
  // uploadCertificationDocument's raw-fetch multipart pattern instead.
  sendAiChatMessage: (params: any) => {
    const endpoint = params.chat_session_id
      ? '/api/ai_services/send_chat_message'
      : '/api/ai_services/create_chat_session';

    return apiClient.post(endpoint, {
      data: {
        type: 'ai_chat_message',
        attributes: params
      }
    });
  },
  sendAiResponseFeedback: (params: any) =>
    apiClient.post('/api/ai_services/send_chat_response_feedback', {
      data: {
        type: 'ai_chat_message',
        attributes: params
      }
    }),
  deleteChatSession: (chatSessionId: string) =>
    apiClient.delete(`/api/ai_services/delete_chat_session?chat_session_id=${chatSessionId}`),
  renameChatSession: (chatSessionId: string, newName: string) =>
    apiClient.patch(`/api/ai_services/update_chat_session?chat_session_id=${chatSessionId}`, {
      data: {
        type: 'ai_chat_session',
        attributes: {
          name: newName
        }
      }
    }),
  createOrUpdateAiChatSession: (params: any) =>
    apiClient.post('/api/ai_services/create_or_update_ai_chat_session', {
      data: {
        type: 'ai_chat_session',
        attributes: params
      }
    }),

  // Registration endpoints
  createUser: (userInfo: any) => {
    // Transform all camelCase keys to snake_case automatically
    const transformedUserInfo = transformKeysToSnakeCase(userInfo);

    // Remove emailConfirm (validation only, not sent to API)
    delete transformedUserInfo.email_confirm;

    // Send in JSONAPI format expected by Connect API
    return apiClient.post('/api/users', {
      data: {
        type: 'user',
        attributes: transformedUserInfo
      }
    });
  },

  // Static pages endpoints
  getStaticPage: (identifier: string) =>
    apiClient.get(`/api/static_pages/${encodeURIComponent(identifier)}`),

  // Product access endpoints
  createAccessTokenForProduct: (code: string) =>
    apiClient.post('/api/products/access_token', { code }).then(response => response.data),
  createCircleAccessToken: () =>
    apiClient.post('/api/products/circle_access_token', {}, { skip401Handling: true }).then(response => response.data),
  // Skip 401 handling to prevent Auth0 redirect loops when session is expired
  // If this fails, the ManageOrganizationLink component will show an error message
  createAdminAccessToken: () =>
    apiClient.post('/api/products/admin_access_token', {}, { skip401Handling: true }).then(response => response.data),

  // Activity tracking endpoints
  logActivity: (product_id: string) =>
    apiClient.post('/api/activities', {
      data: {
        type: 'activity',
        attributes: {
          product_id: product_id,
          activity_id: 7,
          activity: 'Resource tile clicked'
        }
      }
    }),

  // Masquerading endpoints (skip 401 handling during auth initialization)
  startMasquerading: (userId: string, token: string) =>
    apiClient.post(`/api/users/masquerade/${userId}`, {}, { params: { token } }),
  exchangeMasqueradeToken: (token: string) =>
    apiClient.post('/api/users/exchange_masquerade_token', { token }, { skip401Handling: true }),
  stopMasquerading: () => apiClient.post('/api/users/stop_masquerading', {}),
  getMasqueradingStatus: () => apiClient.get('/api/users/masquerading_status', { skip401Handling: true }),

  // Intercom endpoints (skip 401 handling to prevent redirect loops)
  getIntercomSettings: () => apiClient.get('/api/users/intercom_settings', { skip401Handling: true }),

  // Chameleon endpoints (skip 401 handling to prevent redirect loops)
  getChameleonHash: () => apiClient.get('/api/chameleon/hash', { skip401Handling: true }),

  // Countries endpoints
  getCountries: () => apiClient.get('/api/countries'),
  getCountryPricing: (countryCode: string) =>
    apiClient.get(`/api/countries/${countryCode}/pricing`),

  // Onboarding endpoints — reserved for future "Getting Started" dashboard checklist.
  // Backend endpoint exists but is not exposed via UI; uncomment when checklist ships.
  // getOnboardingProgress: (userId: string) =>
  //   apiClient.get(`/api/users/${userId}/onboarding_progress`),

  // Subscription endpoints
  getSubscriptionPricing: () => apiClient.get('/api/subscriptions/pricing'),
  getPractitionerStatus: (userId: string) =>
    apiClient.get(`/api/users/${userId}/practitioner_status`),
  // Prosci Member badge (Credly)
  getMemberBadge: (): Promise<{ data: MemberBadgeStatus }> =>
    apiClient.get('/api/credly_badge'),
  claimMemberBadge: (email?: string): Promise<{ data: MemberBadgeStatus }> =>
    apiClient.post('/api/credly_badge/claim', email ? { email } : {}),
  removeMemberBadge: (): Promise<{ data: MemberBadgeStatus }> =>
    apiClient.delete('/api/credly_badge'),
  checkCertification: () =>
    apiClient.post('/api/users/check_certification'),
  validateCertificationWithAlternateEmail: (data: {
    first_name: string;
    last_name: string;
    alternate_email?: string;
  }) => apiClient.post('/api/users/validate_certification_with_alternate_email', data),
  submitCertificationHelpRequest: (data: {
    first_name: string;
    last_name: string;
    alternate_email?: string;
    additional_details?: string;
    attachment_urls?: string[];
  }) => apiClient.post('/api/users/submit_certification_help_request', data),
  uploadCertificationDocument: async (file: File): Promise<{ data: { success: boolean; url: string; filename: string; content_type: string } }> => {
    const formData = new FormData();
    formData.append('file', file);

    // Get CSRF token for the request
    const csrfResponse = await fetch(`${getApiUrl('/api/auth/csrf_token')}`, {
      credentials: 'include',
    });
    const csrfData = await csrfResponse.json();
    const token = csrfData.csrf_token || '';

    const response = await fetch(getApiUrl('/api/users/upload_certification_document'), {
      method: 'POST',
      headers: {
        'X-CSRF-Token': token,
        // Don't set Content-Type - browser will set it with boundary for FormData
      },
      credentials: 'include',
      body: formData,
    });

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null);
      throw new ApiClientError(
        errorBody?.error || `Upload failed with status ${response.status}`,
        response.status,
        errorBody
      );
    }

    const data = await response.json();
    return { data };
  },
  getUserSubscriptions: (userId: string) =>
    apiClient.get(`/api/subscriptions?user_id=${userId}`),
  createCheckoutSession: (data: {
    user_id: string;
    tier: string;
    billing_country: string;
    success_url: string;
    cancel_url: string;
    pending_certification?: boolean;
  }) => apiClient.post('/api/subscriptions/checkout', data),
  changePlan: (subscriptionId: string, tier: string) =>
    apiClient.post(`/api/subscriptions/${subscriptionId}/change_plan`, {
      tier
    }),
  getSubscriptions: () => apiClient.get('/api/subscriptions'),
  getSubscription: (id: string) => apiClient.get(`/api/subscriptions/${id}`),
  cancelSubscription: (subscriptionId: string) =>
    apiClient.post(`/api/subscriptions/${subscriptionId}/cancel`),
  cancelPendingChange: (subscriptionId: string) =>
    apiClient.post(`/api/subscriptions/${subscriptionId}/cancel_pending_change`),
  restartSubscription: (subscriptionId: string) =>
    apiClient.post(`/api/subscriptions/${subscriptionId}/restart`),
  getPaymentMethod: (subscriptionId: string) =>
    apiClient.get(`/api/subscriptions/${subscriptionId}/payment_method`),
  getInvoices: (subscriptionId: string) =>
    apiClient.get(`/api/subscriptions/${subscriptionId}/invoices`),
  getUpcomingInvoice: (subscriptionId: string) =>
    apiClient.get(`/api/subscriptions/${subscriptionId}/upcoming_invoice`),
  createCustomerPortalSession: (subscriptionId: string, returnUrl: string) =>
    apiClient.post(`/api/subscriptions/${subscriptionId}/customer_portal`, {
      return_url: returnUrl
    }),

  // Granted membership conversion endpoints
  convertGranted: (data: {
    tier: string;
    billing_country: string;
    success_url: string;
    cancel_url: string;
    pending_certification?: boolean;
  }) => apiClient.post('/api/subscriptions/convert_granted', data),
  getProrationPreview: (tier: string) =>
    apiClient.get('/api/subscriptions/proration_preview', { params: { tier } }),
  createHssUpgradeCheckout: (data: {
    success_url: string;
    cancel_url: string;
  }) => apiClient.post('/api/subscriptions/hss_upgrade_checkout', data),

  // Invoice payment endpoints
  createInvoiceCheckout: (data: {
    user_id: string;
    tier: string;
    billing_country: string;
    pending_certification?: boolean;
    customer_name: string;
    organization_name?: string;
    phone?: string;
    po_number?: string;
    billing_address: string;
    vat_id?: string;  // For EU B2B reverse charge
  }) => apiClient.post('/api/subscriptions/invoice_checkout', data),

  // Kaiya tier limit endpoint
  getKaiyaTierLimit: () => apiClient.get('/api/ai_services/kaiya_tier_limit'),

  // Projects the user can tag into a Kaiya chat (session-scoped, is_proxima-filtered)
  getTaggableProjects: () => apiClient.get('/api/ai_services/taggable_projects'),

  removeContextAttachment: (chatSessionId: string, kind: string, refId: string) =>
    apiClient.delete(
      `/api/ai_services/remove_context_attachment?chat_session_id=${encodeURIComponent(chatSessionId)}&kind=${encodeURIComponent(kind)}&ref_id=${encodeURIComponent(refId)}`
    ),

  // Evaluations — slug lookup used by the dashboard tile click
  fetchExamSlug: (productId: number) =>
    apiClient.get<{ slug: string }>('/api/evaluations/exam_sessions/exam_slug', {
      params: { product_id: productId },
    }),
};

export default apiClient;
